import React, { useState, useEffect, useRef } from 'react';
import useWebSocket from '../../hooks/useWebSocket';

// Derive WebSocket base from current page origin so wss:// is used automatically on HTTPS.
// Falls back to localhost for local dev when window is not available (SSR/tests).
const _devWs = (process.env.REACT_APP_API_URL || 'http://localhost:8010').replace(/^http/, 'ws');
const API_WS = typeof window !== 'undefined'
  ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
  : _devWs;

const INDICES = [
  { sym: 'NSE:NIFTY50-INDEX',   label: 'NIFTY 50'  },
  { sym: 'BSE:SENSEX-INDEX',    label: 'SENSEX'     },
  { sym: 'NSE:NIFTYBANK-INDEX', label: 'BANKNIFTY'  },
];

export default function Header({ onMenuClick }) {
  const [ticks,   setTicks]   = useState({});   // sym → ltp
  const [changes, setChanges] = useState({});   // sym → {ch, chp}
  const prevRef               = useRef({});
  const { data: wsData, isConnected } = useWebSocket(`${API_WS}/api/dashboard/ws`);

  useEffect(() => {
    if (!wsData) return;
    if (wsData.ltp) {
      setTicks(prev => {
        prevRef.current = { ...prev };
        return { ...prev, ...wsData.ltp };
      });
    }
    if (wsData.change) {
      setChanges(c => ({ ...c, ...wsData.change }));
    }
  }, [wsData]);

  const tickColor = (sym) => {
    const ch = changes[sym]?.ch ?? 0;
    if (ch > 0)  return 'var(--green)';
    if (ch < 0)  return 'var(--red)';
    const curr = ticks[sym], prev = prevRef.current[sym];
    if (!curr || !prev || curr === prev) return 'var(--text)';
    return curr > prev ? 'var(--green)' : 'var(--red)';
  };

  const tickArrow = (sym) => {
    const ch = changes[sym]?.ch ?? 0;
    if (ch > 0) return '▲';
    if (ch < 0) return '▼';
    const curr = ticks[sym], prev = prevRef.current[sym];
    if (!curr || !prev) return '';
    return curr > prev ? '▲' : curr < prev ? '▼' : '';
  };

  return (
    <header className="app-header">
      {/* Hamburger (mobile only) + Index tickers */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <button className="hamburger" onClick={onMenuClick} aria-label="Toggle menu">
          ☰
        </button>
        <div className="header-tickers" style={{ display: 'flex', gap: 28, alignItems: 'center', overflow: 'hidden', opacity: isConnected ? 1 : 0.45, transition: 'opacity 0.4s' }}>
        {INDICES.map(({ sym, label }) => {
          const ltp    = ticks[sym];
          const ch     = changes[sym]?.ch  ?? null;
          const chp    = changes[sym]?.chp ?? null;
          const color  = isConnected ? tickColor(sym) : 'var(--muted)';
          const arrow  = isConnected ? tickArrow(sym) : '';
          const hasChg = ch !== null && ch !== 0;

          return (
            <div key={sym} style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              {/* Label */}
              <span style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {label}
              </span>

              {/* LTP + arrow */}
              <span style={{ fontSize: 14, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: 3 }}>
                {ltp
                  ? ltp.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                  : <span style={{ color: 'var(--muted)', fontWeight: 400 }}>—</span>
                }
                {ltp && arrow && (
                  <span style={{ fontSize: 10, lineHeight: 1 }}>{arrow}</span>
                )}
              </span>

              {/* Change pts / % */}
              <span style={{ fontSize: 10, fontVariantNumeric: 'tabular-nums', color: hasChg ? color : 'var(--muted)', fontWeight: hasChg ? 600 : 400 }}>
                {hasChg
                  ? `${ch > 0 ? '+' : ''}${ch.toFixed(2)} (${chp > 0 ? '+' : ''}${chp.toFixed(2)}%)`
                  : ltp ? '— pts' : ''
                }
              </span>
            </div>
          );
        })}
        </div>
      </div>

      {/* Connection status + clock */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Clock />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
            background: isConnected ? 'var(--green)' : 'var(--muted)',
            boxShadow:  isConnected ? '0 0 6px var(--green)' : 'none',
          }} />
          <span style={{ color: 'var(--muted)' }}>{isConnected ? 'Live' : 'Offline'}</span>
        </div>
      </div>
    </header>
  );
}

function Clock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () =>
      setTime(new Date().toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        timeZone: 'Asia/Kolkata',
      }));
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
      🕐 IST {time}
    </span>
  );
}
